<template>
  <div class="sf-page">
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <v-snackbar color="success"
                right
                top
                v-model="confirmationSnackbar">
      {{ $t('saved') }}
    </v-snackbar>

    <top-header v-bind:subtitle="$t('subtitle')"
                v-bind:title="$t('floorPlan')">
      <template slot="right-button">
        <v-chip class="sf-pill sf-pill--success"
                small>{{ $tc('assigned', stats.assigned, { count: stats.assigned }) }}</v-chip>
        <v-chip class="sf-pill sf-pill--neutral ml-1"
                small>{{ $tc('free', stats.free, { count: stats.free }) }}</v-chip>
        <v-chip class="sf-pill sf-pill--accent ml-1"
                small
                v-if="stats.conflicts > 0">{{ $tc('conflicts', stats.conflicts, { count: stats.conflicts }) }}</v-chip>
      </template>
    </top-header>

    <div class="sf-container">
      <v-alert class="sf-alert mb-4"
               text
               type="warning"
               v-if="unknownNumbers.length > 0">
        {{ $t('unknownNumbers') }}
        <ul class="mb-0 mt-1">
          <li v-bind:key="item.supplier.id"
              v-for="item in unknownNumbers">
            <router-link v-bind:to="{ name: 'administration-supplier', params: { id: item.supplier.id } }">{{ displayName(item.supplier) }}</router-link>:
            {{ item.numbers.join(', ') }}
          </li>
        </ul>
      </v-alert>

      <v-alert class="sf-alert mb-4"
               dismissible
               text
               type="info"
               v-if="pendingSupplier != null"
               v-on:input="pendingSupplier = null">
        {{ $t('pendingHint', { name: displayName(pendingSupplier) }) }}
      </v-alert>

      <div class="sf-floor-plan-admin">
        <v-card class="sf-card sf-floor-plan-admin__plan"
                flat>
          <div class="sf-floor-plan-admin__canvas">
            <floor-plan mode="admin"
                        ref="plan"
                        v-bind:assignments="assignments"
                        v-bind:plan="plan"
                        v-bind:selected="selectedNumber"
                        v-on:select="onSelectStand" />
          </div>
          <p class="sf-floor-plan-admin__hint">{{ $t('hint') }}</p>
        </v-card>

        <v-card class="sf-card sf-floor-plan-admin__side"
                flat>
          <div class="sf-floor-plan-admin__side-title">
            <h3 class="sf-section-title mb-0">{{ $t('withoutStand') }}</h3>
            <span class="sf-toolbar__hint">{{ suppliersWithoutStand.length }}</span>
          </div>
          <v-text-field class="sf-search mb-2"
                        clearable
                        dense
                        flat
                        hide-details
                        prepend-inner-icon="ti-search"
                        solo
                        v-bind:placeholder="$t('search')"
                        v-model="sideSearch" />
          <v-list class="sf-floor-plan-admin__list"
                  dense>
            <v-list-item v-bind:class="{ 'sf-floor-plan-admin__item--pending': pendingSupplier != null && pendingSupplier.id === supplier.id }"
                         v-bind:key="supplier.id"
                         v-for="supplier in filteredSuppliersWithoutStand"
                         v-on:click="togglePending(supplier)">
              <v-list-item-content>
                <v-list-item-title>{{ displayName(supplier) }}</v-list-item-title>
                <v-list-item-subtitle>{{ supplier.companyName }} · {{ $t(`state.${supplier.state}`) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small>{{ pendingSupplier != null && pendingSupplier.id === supplier.id ? 'ti-check' : 'ti-map-pin-plus' }}</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="filteredSuppliersWithoutStand.length === 0">
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('noneWithoutStand') }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </div>
    </div>

    <v-dialog max-width="520"
              v-model="dialog">
      <v-card class="sf-dialog"
              v-if="selectedNumber != null">
        <v-card-title class="sf-dialog__title">
          {{ $t('stand') }} {{ selectedNumber }}
          <span class="sf-dialog__label"
                v-if="selectedLabel != null">· {{ selectedLabel }}</span>
        </v-card-title>
        <v-card-text>
          <div class="mb-4"
               v-if="currentHolders.length > 0">
            <div class="sf-section-title">{{ $tc('currentlyAssigned', currentHolders.length) }}</div>
            <v-list class="pa-0"
                    dense>
              <v-list-item class="px-0"
                           v-bind:key="holder.id"
                           v-for="holder in currentHolders">
                <v-list-item-content>
                  <v-list-item-title>{{ displayName(holder) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ holder.companyName }} · {{ $t(`state.${holder.state}`) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn color="error"
                         small
                         text
                         v-bind:loading="saving"
                         v-on:click="unassign(holder)">
                    <v-icon left
                            small>ti-x</v-icon>
                    {{ $t('removeAssignment') }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
          <div class="sf-section-title">{{ $t('assignSupplier') }}</div>
          <v-autocomplete clearable
                          dense
                          hide-details="auto"
                          item-text="text"
                          item-value="id"
                          outlined
                          return-object
                          v-bind:items="supplierItems"
                          v-bind:label="$t('supplier')"
                          v-model="dialogSupplier">
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <p class="sf-dialog__note mt-3 mb-0"
             v-if="dialogSupplier != null && currentHolders.length > 0 && !currentHolders.some(h => h.id === dialogSupplier.id)">
            {{ $t('replaceNote', { names: currentHolders.map(displayName).join(', ') }) }}
          </p>
        </v-card-text>
        <v-card-actions class="sf-dialog__actions">
          <v-spacer />
          <v-btn text
                 v-on:click="dialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary"
                 depressed
                 v-bind:disabled="dialogSupplier == null"
                 v-bind:loading="saving"
                 v-on:click="assign(dialogSupplier, true)">{{ $t('assign') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import settings from '../../settings'
  import plan from '@/assets/floorplan.json'
  import { buildAssignments, formatBoothNumbers, parseBoothNumbers, planNumbers, supplierDisplayName } from '@/lib/floorplan/booths'

  import FloorPlan from '@/components/floorplan/FloorPlan'
  import TopHeader from '@/components/common/TopHeader'

  export default {
    components: {
      FloorPlan,
      TopHeader
    },
    computed: {
      assignments() {
        return buildAssignments(this.suppliers, { apiUri: settings.apiUri })
      },
      currentHolders() {
        const assignment = this.selectedNumber != null ? this.assignments[this.selectedNumber] : null

        return assignment != null ? assignment.suppliers : []
      },
      filteredSuppliersWithoutStand() {
        const search = (this.sideSearch || '').trim().toLowerCase()

        if (search.length === 0) {
          return this.suppliersWithoutStand
        }

        return this.suppliersWithoutStand.filter(s => `${s.companyName || ''} ${s.supplierGroup || ''} ${s.id}`.toLowerCase().includes(search))
      },
      knownNumbers() {
        return new Set(planNumbers(this.plan).map(item => item.number))
      },
      selectedLabel() {
        const item = planNumbers(this.plan).find(i => i.number === this.selectedNumber)

        return item != null ? item.label : null
      },
      stats() {
        const numbers = [...this.knownNumbers]
        const assigned = numbers.filter(n => this.assignments[n] != null).length

        return {
          assigned,
          free: numbers.length - assigned,
          conflicts: Object.values(this.assignments).filter(a => a.conflict).length
        }
      },
      supplierItems() {
        return [...this.suppliers]
          .sort((a, b) => supplierDisplayName(a).localeCompare(supplierDisplayName(b)))
          .map(s => ({
            ...s,
            text: supplierDisplayName(s),
            subtitle: `${s.companyName || ''} · ${this.$t(`state.${s.state}`)}${s.boothNumber ? ` · ${this.$t('stand')} ${s.boothNumber}` : ''}`
          }))
      },
      suppliersWithoutStand() {
        return [...this.suppliers]
          .filter(s => parseBoothNumbers(s.boothNumber).length === 0 && s.state !== 'declined')
          .sort((a, b) => supplierDisplayName(a).localeCompare(supplierDisplayName(b)))
      },
      unknownNumbers() {
        return this.suppliers
          .map(supplier => ({ supplier, numbers: parseBoothNumbers(supplier.boothNumber).filter(n => !this.knownNumbers.has(n)) }))
          .filter(item => item.numbers.length > 0)
      },
      ...mapGetters([
        'suppliers'
      ])
    },
    async created() {
      this.loading = true
      await this.$store.dispatch('loadSuppliers')
      this.loading = false
    },
    data: () => ({
      confirmationSnackbar: false,
      dialog: false,
      dialogSupplier: null,
      loading: false,
      pendingSupplier: null,
      plan,
      saving: false,
      selectedNumber: null,
      sideSearch: null
    }),
    methods: {
      async assign(supplier, replace) {
        if (supplier == null || this.selectedNumber == null) {
          return
        }

        this.saving = true

        try {
          if (replace) {
            for (const holder of this.currentHolders) {
              if (holder.id !== supplier.id) {
                await this.save(holder, parseBoothNumbers(holder.boothNumber).filter(n => n !== this.selectedNumber))
              }
            }
          }

          const numbers = parseBoothNumbers(supplier.boothNumber)

          if (!numbers.includes(this.selectedNumber)) {
            await this.save(supplier, [...numbers, this.selectedNumber])
          }

          this.confirmationSnackbar = true
          this.dialog = false
          this.pendingSupplier = null
        } finally {
          this.saving = false
        }
      },
      displayName(supplier) {
        return supplierDisplayName(supplier)
      },
      onSelectStand({ number }) {
        this.selectedNumber = number

        if (this.pendingSupplier != null) {
          const pending = this.$store.getters.supplier(this.pendingSupplier.id) || this.pendingSupplier

          this.assign(pending, true)

          return
        }

        this.dialogSupplier = null
        this.dialog = true
      },
      async save(supplier, numbers) {
        const current = this.$store.getters.supplier(supplier.id) || supplier

        await this.$store.dispatch('updateSupplier', { supplier: { ...current, boothNumber: numbers.length > 0 ? formatBoothNumbers(numbers) : null } })
      },
      togglePending(supplier) {
        this.pendingSupplier = this.pendingSupplier != null && this.pendingSupplier.id === supplier.id ? null : supplier
      },
      async unassign(holder) {
        this.saving = true

        try {
          await this.save(holder, parseBoothNumbers(holder.boothNumber).filter(n => n !== this.selectedNumber))
          this.confirmationSnackbar = true

          if (this.currentHolders.length === 0) {
            this.dialog = false
          }
        } finally {
          this.saving = false
        }
      }
    },
    name: 'FloorPlanAdmin',
    watch: {
      dialog(value) {
        if (!value) {
          this.selectedNumber = null
        }
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "assign": "Zuweisen",
      "assignSupplier": "Lieferant zuweisen",
      "assigned": "{count} belegt",
      "cancel": "Abbrechen",
      "conflicts": "{count} Konflikt | {count} Konflikt | {count} Konflikte",
      "currentlyAssigned": "Aktuell zugewiesen | Aktuell zugewiesen (mehrfach belegt)",
      "floorPlan": "Lageplan",
      "free": "{count} frei",
      "hint": "Klicken Sie auf einen Stand, um einen Lieferanten zuzuweisen. Rot markierte Stände sind mehrfach belegt. Die Standnummern können auch direkt am Lieferanten bearbeitet werden.",
      "noneWithoutStand": "Alle Lieferanten haben einen Stand.",
      "pendingHint": "„{name}“ ausgewählt – klicken Sie jetzt im Plan auf den gewünschten Stand.",
      "removeAssignment": "Entfernen",
      "replaceNote": "Die bisherige Zuweisung ({names}) wird entfernt.",
      "saved": "Gespeichert.",
      "search": "Suchen",
      "stand": "Stand",
      "state": {
        "accepted": "Angenommen",
        "declined": "Abgelehnt",
        "invited": "Eingeladen",
        "open": "Offen"
      },
      "subtitle": "Standnummern den Lieferanten zuweisen. Kunden sehen nur angenommene Lieferanten im Plan.",
      "supplier": "Lieferant",
      "unknownNumbers": "Folgende Lieferanten haben Standnummern, die es im Plan nicht gibt:",
      "withoutStand": "Ohne Stand"
    },
    "en": {
      "assign": "Assign",
      "assignSupplier": "Assign supplier",
      "assigned": "{count} assigned",
      "cancel": "Cancel",
      "conflicts": "{count} conflict | {count} conflict | {count} conflicts",
      "currentlyAssigned": "Currently assigned | Currently assigned (multiple)",
      "floorPlan": "Floor plan",
      "free": "{count} free",
      "hint": "Click a booth to assign a supplier. Red booths are assigned multiple times. Booth numbers can also be edited on the supplier page.",
      "noneWithoutStand": "All suppliers have a booth.",
      "pendingHint": "“{name}” selected – now click the booth in the plan.",
      "removeAssignment": "Remove",
      "replaceNote": "The current assignment ({names}) will be removed.",
      "saved": "Saved.",
      "search": "Search",
      "stand": "Booth",
      "state": {
        "accepted": "Accepted",
        "declined": "Declined",
        "invited": "Invited",
        "open": "Open"
      },
      "subtitle": "Assign booth numbers to suppliers. Customers only see accepted suppliers on the plan.",
      "supplier": "Supplier",
      "unknownNumbers": "The following suppliers have booth numbers that do not exist on the plan:",
      "withoutStand": "Without booth"
    }
  }
</i18n>

<style lang="scss" scoped>
  .sf-floor-plan-admin {
    align-items: stretch;
    display: grid;
    gap: var(--sf-space-4);
    grid-template-columns: minmax(0, 1fr) 300px;
  }

  .sf-floor-plan-admin__plan {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .sf-floor-plan-admin__canvas {
    height: clamp(420px, calc(100vh - 300px), 820px);
  }

  .sf-floor-plan-admin__hint {
    color: var(--sf-text-muted);
    font-size: .8125rem;
    margin: 0;
    padding: var(--sf-space-3) var(--sf-space-4);
  }

  .sf-floor-plan-admin__side {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: var(--sf-space-4);
  }

  .sf-floor-plan-admin__side-title {
    align-items: baseline;
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--sf-space-3);
  }

  .sf-floor-plan-admin__list {
    flex: 1 1 auto;
    max-height: 640px;
    overflow-y: auto;
  }

  .sf-floor-plan-admin__item--pending {
    background: var(--sf-primary-soft);
    border-radius: var(--sf-radius-sm);
  }

  .sf-dialog__label {
    color: var(--sf-text-secondary);
    font-weight: 400;
    margin-left: 6px;
  }

  .sf-dialog__note {
    color: var(--sf-warning);
    font-size: .8125rem;
  }

  @media (max-width: 959px) {
    .sf-floor-plan-admin {
      grid-template-columns: 1fr;
    }

    .sf-floor-plan-admin__canvas {
      height: calc(100vh - 260px);
      min-height: 420px;
    }
  }
</style>
